import type {
  ControlCenterLicenseType,
  ControlCenterMunicipality,
  ControlCenterMunicipalityLevel,
} from '../types/controlCenter'
import type { TigerwebSearchHit } from '../services/tigerwebBoundaryService'

/** Map marker / swatch color for Census places not in the license DB. */
export const EXTERNAL_MUNICIPALITY_COLOR = '#dc2626'

const STATE_FIPS_TO_ABBR: Record<string, string> = {
  '01': 'AL',
  '02': 'AK',
  '04': 'AZ',
  '05': 'AR',
  '06': 'CA',
  '08': 'CO',
  '09': 'CT',
  '10': 'DE',
  '11': 'DC',
  '12': 'FL',
  '13': 'GA',
  '15': 'HI',
  '16': 'ID',
  '17': 'IL',
  '18': 'IN',
  '19': 'IA',
  '20': 'KS',
  '21': 'KY',
  '22': 'LA',
  '23': 'ME',
  '24': 'MD',
  '25': 'MA',
  '26': 'MI',
  '27': 'MN',
  '28': 'MS',
  '29': 'MO',
  '30': 'MT',
  '31': 'NE',
  '32': 'NV',
  '33': 'NH',
  '34': 'NJ',
  '35': 'NM',
  '36': 'NY',
  '37': 'NC',
  '38': 'ND',
  '39': 'OH',
  '40': 'OK',
  '41': 'OR',
  '42': 'PA',
  '44': 'RI',
  '45': 'SC',
  '46': 'SD',
  '47': 'TN',
  '48': 'TX',
  '49': 'UT',
  '50': 'VT',
  '51': 'VA',
  '53': 'WA',
  '54': 'WV',
  '55': 'WI',
  '56': 'WY',
}

export const STATE_ABBR_TO_FIPS: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_FIPS_TO_ABBR).map(([fips, abbr]) => [abbr, fips]),
)

export function stateAbbrFromCensusHit(hit: TigerwebSearchHit): string | null {
  if (hit.stateAbbr) return hit.stateAbbr
  if (hit.stateFips && STATE_FIPS_TO_ABBR[hit.stateFips]) {
    return STATE_FIPS_TO_ABBR[hit.stateFips]
  }
  return null
}

export function censusMunicipalityId(level: string, geoid: string): string {
  return `census:${level}:${geoid}`
}

export function municipalityFromCensusHit(
  hit: TigerwebSearchHit,
  coords: { lat: number; lng: number },
): ControlCenterMunicipality {
  const state = stateAbbrFromCensusHit(hit) || '—'
  return {
    id: censusMunicipalityId(hit.level, hit.geoid),
    name: hit.basename || hit.name,
    state,
    level: hit.level,
    lat: coords.lat,
    lng: coords.lng,
    censusGeoid: hit.geoid,
    licenseType: 'other',
    licenseNumber: '',
    expirationDate: '',
    obtainedDate: null,
    annualCost: null,
    qualifier: null,
    licenseUrl: null,
    licenseImageFront: null,
    licenseImageBack: null,
    isExternal: true,
  }
}

function toIsoDate(value: unknown): string | null {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const d = value.slice(0, 10)
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null
  }
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }
  const parsed = new Date(String(value))
  if (!Number.isFinite(parsed.getTime())) return null
  return parsed.toISOString().slice(0, 10)
}

function asLevel(value: unknown): ControlCenterMunicipalityLevel {
  const v = String(value || '').toLowerCase()
  if (v === 'city' || v === 'county' || v === 'state' || v === 'federal') return v
  return 'state'
}

function asLicenseType(value: unknown): ControlCenterLicenseType {
  const v = String(value || '').toLowerCase()
  if (
    v === 'sos_registration' ||
    v === 'business' ||
    v === 'contractor' ||
    v === 'electrical' ||
    v === 'other'
  ) {
    return v
  }
  return 'other'
}

export function licenseTypeLabel(type: ControlCenterLicenseType): string {
  switch (type) {
    case 'sos_registration':
      return 'SOS registration'
    case 'business':
      return 'Business'
    case 'contractor':
      return 'Contractor'
    case 'electrical':
      return 'Electrical'
    default:
      return 'Other'
  }
}

export function mapMunicipalityLicenseRow(row: Record<string, unknown>): ControlCenterMunicipality {
  const expiration = toIsoDate(row.expirationDate) || ''
  return {
    id: String(row._id ?? row.id ?? ''),
    name: String(row.name || '').trim() || 'Unknown',
    state: String(row.state || '').trim().toUpperCase(),
    level: asLevel(row.level),
    lat: Number(row.lat),
    lng: Number(row.lng),
    censusGeoid: row.censusGeoid != null && String(row.censusGeoid).trim()
      ? String(row.censusGeoid).trim()
      : null,
    licenseType: asLicenseType(row.licenseType),
    licenseNumber: String(row.licenseNumber || '').trim(),
    expirationDate: expiration,
    obtainedDate: toIsoDate(row.obtainedDate),
    annualCost:
      row.annualCost == null || row.annualCost === ''
        ? null
        : Number(row.annualCost),
    qualifier: row.qualifier != null && String(row.qualifier).trim()
      ? String(row.qualifier).trim()
      : null,
    licenseUrl: row.licenseUrl != null && String(row.licenseUrl).trim()
      ? String(row.licenseUrl).trim()
      : null,
    licenseImageFront:
      row.licenseImageFront != null && String(row.licenseImageFront).trim()
        ? String(row.licenseImageFront).trim()
        : null,
    licenseImageBack:
      row.licenseImageBack != null && String(row.licenseImageBack).trim()
        ? String(row.licenseImageBack).trim()
        : null,
  }
}
